/**
 * Connection-selection store — persistent sibling of `connectionHoverStore`.
 *
 * Clicking ANY piece of a connection (the LCA edge, a wall ghost, a segment on
 * any open level) selects the whole connection as a UNIT: every piece whose id
 * intersects the selected set is emphasised and stays that way until another
 * connection is clicked or the canvas is cleared — across sub-space AND up-space
 * levels, and across every connection when a bundled piece is clicked.
 *
 * Same external-store shape as the hover store (stable provider, Set snapshot
 * with a fresh ref only on change), plus a per-piece boolean hook so a memoised
 * piece re-renders only when its own selected state flips.
 */
import { createContext, useContext, useSyncExternalStore } from "react";

export interface ConnectionSelectionStore {
  subscribe(onChange: () => void): () => void;
  getSelected(): ReadonlySet<string>;
  /** Replace the selected connection-id set (null/[] clears). */
  setSelected(ids: readonly string[] | null): void;
}

const EMPTY: ReadonlySet<string> = new Set();

export function createConnectionSelectionStore(): ConnectionSelectionStore {
  let selected: ReadonlySet<string> = EMPTY;
  const listeners = new Set<() => void>();
  const sameAs = (ids: readonly string[]) =>
    ids.length === selected.size && ids.every((id) => selected.has(id));
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getSelected() {
      return selected;
    },
    setSelected(ids) {
      if (!ids || ids.length === 0) {
        if (selected.size === 0) return;
        selected = EMPTY;
      } else {
        if (sameAs(ids)) return;
        selected = new Set(ids);
      }
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<ConnectionSelectionStore | null>(null);
export const ConnectionSelectionProvider = Ctx.Provider;

const NOOP: ConnectionSelectionStore = {
  subscribe: () => () => {},
  getSelected: () => EMPTY,
  setSelected: () => {},
};

export function useConnectionSelectionStore(): ConnectionSelectionStore {
  return useContext(Ctx) ?? NOOP;
}

/** Per-piece boolean: is any of THESE ids selected? Re-renders the piece only
 *  when its own selected state flips. */
export function useConnectionSelected(ids: readonly string[]): boolean {
  const store = useConnectionSelectionStore();
  return useSyncExternalStore(
    store.subscribe,
    () => {
      const s = store.getSelected();
      for (const id of ids) if (s.has(id)) return true;
      return false;
    },
    () => false,
  );
}

/** Layer-level: the whole selected set (for a component rendering many pieces
 *  inline, e.g. PortsLayer). */
export function useSelectedConnections(): ReadonlySet<string> {
  const store = useConnectionSelectionStore();
  return useSyncExternalStore(store.subscribe, store.getSelected, () => EMPTY);
}
